import { atom } from 'jotai';
import { loadable } from 'jotai/utils';
import { atomWithLoadable, mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import type {
  AreasQueryResponse,
  DevelopmentAreaQueryResponse,
  SearchResult,
} from 'types/api-responses';
import type {
  CategoriesResponse,
  FetchGeometryParams,
  StatesResponse,
  ToastNotification,
  UnitTypesResponse,
  WellSearchResponse,
} from './types';

// ─────────────────────────────────────
// Toast notification (shared across scenario forms)
// ─────────────────────────────────────

export const toastAtom = atom<ToastNotification | null>(null);

// ─────────────────────────────────────
// Lookup atoms — auto-fetch on mount
// ─────────────────────────────────────

/**
 * Fetches distinct area geometry categories for the "Area Category" dropdown.
 * @return {CategoriesResponse} The categories response
 */
export const [categoriesBaseAtom, categoriesLoadableAtom] = atomWithLoadable(
  async () => {
    const response = await request.get<CategoriesResponse>(
      '/queries/area_lookups/categories'
    );
    return response as CategoriesResponse;
  }
);

/**
 * Fetches distinct development area unit types for the "Unit Type" dropdown.
 * @return {UnitTypesResponse} The unit types response
 */
export const [unitTypesBaseAtom, unitTypesLoadableAtom] = atomWithLoadable(
  async () => {
    const response = await request.get<UnitTypesResponse>(
      '/queries/area_lookups/unit_types'
    );
    return response as UnitTypesResponse;
  }
);

// ─────────────────────────────────────
// Area typeahead search atoms
// ─────────────────────────────────────

/**
 * Row cap for the typeahead dropdowns. Sent as snake_case `page_size` to match
 * the controllers' permitted params directly. (The backend's rails-boost key
 * transform underscores incoming keys anyway, so camelCase `pageSize` would also
 * be honored — this is a consistency choice, not a functional requirement.)
 */
const TYPEAHEAD_PAGE_SIZE = 10;

/** The selected area category. */
export const areaCategoryAtom = atom('');

/** The area search query. undefined = no fetch, '' = fetch all, 'text' = filter. */
export const areaQueryAtom = atom<string | undefined>(undefined);

/** Reactive fetch — runs when query becomes a string and category is set. */
const areaSearchBaseAtom = atom(async (get) => {
  const category = get(areaCategoryAtom);
  const query = get(areaQueryAtom);
  if (!category || query === undefined) return null;

  const response = await request.post<
    SearchResult<AreasQueryResponse>,
    unknown
  >(
    '/queries/area_geometries',
    {
      category,
      text: query || undefined,
      page: 1,
      page_size: TYPEAHEAD_PAGE_SIZE,
    },
    new AbortController()
  );

  if (response.type !== 'success') {
    throw new Error('Failed to search area geometries');
  }
  return response.data;
});

export const areaSearchLoadableAtom = loadable(areaSearchBaseAtom);

// ─────────────────────────────────────
// Unit typeahead search atoms
// ─────────────────────────────────────

/** The selected unit type. */
export const unitTypeAtom = atom('');

/** The unit search query. undefined = no fetch, '' = fetch all, 'text' = filter. */
export const unitQueryAtom = atom<string | undefined>(undefined);

/** Reactive fetch — runs when query becomes a string and unit type is set. */
const unitSearchBaseAtom = atom(async (get) => {
  const unitType = get(unitTypeAtom);
  const query = get(unitQueryAtom);
  if (!unitType || query === undefined) return null;

  const response = await request.post<
    SearchResult<DevelopmentAreaQueryResponse>,
    unknown
  >(
    '/queries/development_areas',
    {
      unit_type: unitType,
      text: query || undefined,
      page: 1,
      page_size: TYPEAHEAD_PAGE_SIZE,
    },
    new AbortController()
  );

  if (response.type !== 'success') {
    throw new Error('Failed to search development areas');
  }
  return response.data;
});

export const unitSearchLoadableAtom = loadable(unitSearchBaseAtom);

// ─────────────────────────────────────
// Well (slot) search atoms
// ─────────────────────────────────────

/** The well search query. undefined = no fetch, '' = no fetch, 'text' = fetch. */
export const wellQueryAtom = atom<string | undefined>(undefined);

/** Reactive fetch — runs when query is at least 2 characters. */
const wellSearchBaseAtom = atom(async (get) => {
  const query = get(wellQueryAtom);
  if (query === undefined || query.length < 2) return null;

  const response = await request.get<WellSearchResponse>(
    `/queries/area_lookups/wells?text=${encodeURIComponent(query)}`
  );
  return response as WellSearchResponse;
});

export const wellSearchLoadableAtom = loadable(wellSearchBaseAtom);

// ─────────────────────────────────────
// State search atoms
// ─────────────────────────────────────

/** The state search query. undefined = no fetch, '' = fetch all, 'text' = filter. */
export const stateQueryAtom = atom<string | undefined>(undefined);

/** Tracks the in-flight AbortController so each new search cancels the previous. */
let stateAbortController: AbortController | null = null;

/** Reactive fetch — runs when query becomes a string. Aborts previous in-flight request. */
const stateSearchBaseAtom = atom(async (get) => {
  const query = get(stateQueryAtom);
  if (query === undefined) return null;

  if (stateAbortController) stateAbortController.abort();
  stateAbortController = new AbortController();

  const response = await request.get<StatesResponse>(
    `/queries/area_lookups/states?text=${encodeURIComponent(query)}`,
    stateAbortController
  );
  return response as StatesResponse;
});

export const stateSearchLoadableAtom = loadable(stateSearchBaseAtom);

// ─────────────────────────────────────
// Geometry fetch — per area entry
// ─────────────────────────────────────

/** Cache of fetched geometries, keyed by area entry ID. */
export const areaGeometriesAtom = atom<Record<number, GeoJSON.Geometry>>({});

/**
 * Fetches geometry for a selected area/unit/slot and stores it in the cache.
 * For slots, fetches the well stick geometry from the features endpoint.
 * @param {FetchGeometryParams} params - The entry ID, geometry type, and configurable ID
 * @return {GeoJSON.Geometry} The fetched geometry
 */
export const [fetchGeometryAtom, fetchGeometryLoadableAtom] =
  mutationAtomWithLoadable(
    async (
      _get,
      set,
      params: FetchGeometryParams
    ): Promise<GeoJSON.Geometry> => {
      try {
        let geometry: GeoJSON.Geometry;

        if (params.type === 'slot') {
          const response = await request.get<{
            stick: GeoJSON.Geometry | null;
          }>(`/wells/${encodeURIComponent(params.uwi ?? '')}/features`);
          const features = response as { stick: GeoJSON.Geometry | null };
          if (!features.stick) throw new Error('Well has no stick geometry');
          geometry = features.stick;
        } else {
          const pathMap: Record<string, string> = {
            area: `/area_geometries/${params.configurableId}`,
            unit: `/development_areas/${params.configurableId}`,
            state: `/queries/area_lookups/state_geometry?id=${params.configurableId}`,
          };
          const path = pathMap[params.type];
          const response = await request.get<{ geometry: GeoJSON.Geometry }>(
            path
          );
          geometry = (response as { geometry: GeoJSON.Geometry }).geometry;
        }

        set(areaGeometriesAtom, (prev) => ({
          ...prev,
          [params.entryId]: geometry,
        }));

        return geometry;
      } catch {
        set(toastAtom, {
          message: 'Failed to load geometry',
          type: 'error',
        });
        throw new Error('Failed to load geometry');
      }
    }
  );
